import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import {
  Facebook, Instagram, Trash2, Loader2, ExternalLink, Link2Off,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Eye, EyeOff,
  Star,
} from "lucide-react";
import { api } from "@/lib/api";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Settings section: connected Facebook Pages + linked Instagram
 * Business/Creator accounts. Users click "Connect Facebook" → get
 * redirected to Facebook → grant permission → land back on /settings
 * with `?meta_status=ok` (or `error`/`denied`) which we flash as a toast.
 */
export default function SocialConnectionsSection() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [diagOpen, setDiagOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["social-connections"],
    queryFn: api.listSocialConnections,
  });

  const { data: lastAttempt } = useQuery({
    queryKey: ["meta-last-attempt"],
    queryFn: api.metaLastAttempt,
    refetchOnMount: true,
  });

  // Consume `meta_status` from the OAuth-callback redirect so we can
  // show the editor a confirmation toast (persistent, per new Toaster
  // config). We strip the params right after so a page refresh doesn't
  // re-fire the toast.
  useEffect(() => {
    const status = sp.get("meta_status");
    if (!status) return;
    const msg = sp.get("meta_msg") || "";
    if (status === "ok") {
      toast.success(tr("Facebook connected — {msg}", { msg: msg || tr("pages linked") }));
      qc.invalidateQueries({ queryKey: ["social-connections"] });
    } else if (status === "denied") {
      toast.warning(tr("You denied the Facebook connection ({msg})", { msg }));
    } else {
      toast.error(tr("Facebook connection failed — {msg}", { msg }));
    }
    const clean = new URLSearchParams(sp);
    clean.delete("meta_status");
    clean.delete("meta_msg");
    setSp(clean, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useMutation({
    mutationFn: api.metaAuthUrl,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          tr("Could not start Facebook connection"),
      ),
  });

  const disconnect = useMutation({
    mutationFn: (pageId) => api.deleteSocialConnection(pageId),
    onSuccess: () => {
      toast.success(tr("Page disconnected"));
      qc.invalidateQueries({ queryKey: ["social-connections"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Disconnect failed")),
  });

  const setVisibility = useMutation({
    mutationFn: ({ pageId, hidden }) =>
      api.setSocialConnectionVisibility(pageId, hidden),
    onSuccess: (_, vars) => {
      toast.success(
        vars.hidden
          ? tr("Hidden from this property's share menus")
          : tr("Visible on this property again"),
      );
      qc.invalidateQueries({ queryKey: ["social-connections"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Visibility toggle failed")),
  });

  // Pin the Page the share panels pre-select for this property. The
  // backend rejects hidden or disconnected Pages, so the only thing we
  // do here is surface its message.
  const setDefault = useMutation({
    mutationFn: (pageId) => api.setSocialConnectionDefault(pageId),
    onSuccess: () => {
      toast.success(tr("Default Page updated for this property"));
      qc.invalidateQueries({ queryKey: ["social-connections"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Could not set default Page")),
  });

  const items = data?.items || [];
  const diag = lastAttempt?.last_attempt;

  return (
    <div data-testid="social-connections-section" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-[var(--ink-muted)] max-w-lg">{trRich("Connect the Facebook Pages this newspaper uses. Instagram Business/Creator accounts linked to a Facebook Page are picked up automatically. Use <0>Hide here</0> to keep a Page connected but out of this property’s share menus — handy when the same Facebook account owns Pages for multiple properties and you don’t want to publish to the wrong one. The Page marked <1>Default</1> is the one every share panel opens on for this property.", null, [<em />, <em />])}</p>
        </div>
        <button
          data-testid="connect-facebook-btn"
          onClick={() => connect.mutate()}
          disabled={connect.isPending}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-[#1877F2] text-white hover:bg-[#166dd8] disabled:opacity-60"
        >
          {connect.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Facebook className="w-4 h-4" />
          )}{tr("Connect Facebook")}<ExternalLink className="w-3 h-3 opacity-70" />
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-[var(--ink-muted)] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Loading connections…")}</div>
      ) : items.length === 0 ? (
        <div
          data-testid="social-connections-empty"
          className="border border-dashed border-[var(--border)] p-6 text-sm text-[var(--ink-muted)]"
        >
          <div className="flex items-center gap-2 mb-1">
            <Link2Off className="w-4 h-4" />
            {trRich("<0>No pages connected yet</0>", null, [<span className="font-medium text-[var(--ink)]" />])}
          </div>{trRich("Click <0>Connect Facebook</0> to link this newspaper’s Facebook Pages. Your Instagram Business accounts will be picked up automatically if they’re linked to a Page.", null, [<em />])}</div>
      ) : (
        <ul
          data-testid="social-connections-list"
          className="divide-y divide-[var(--border)] border border-[var(--border)]"
        >
          {items.map((c) => (
            <li
              key={c.page_id}
              data-testid={`social-connection-${c.page_id}`}
              className={
                "flex items-start gap-3 p-3 " +
                (c.hidden ? "bg-neutral-50 opacity-70" : "bg-white")
              }
            >
              <div className="shrink-0 w-9 h-9 grid place-items-center bg-[#1877F2]/10 border border-[#1877F2]/20">
                <Facebook className="w-4 h-4 text-[#1877F2]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  <span className="truncate">{c.page_name}</span>
                  {c.is_default && !c.hidden && (
                    <span
                      data-testid={`social-default-tag-${c.page_id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 font-mono-display"
                    >
                      <Star className="w-2.5 h-2.5 fill-current" />{tr("default")}</span>
                  )}
                  {c.hidden && (
                    <span
                      data-testid={`social-hidden-tag-${c.page_id}`}
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-neutral-300 bg-white text-neutral-600 font-mono-display"
                    >{tr("hidden here")}</span>
                  )}
                </div>
                <div className="text-[11px] text-[var(--ink-muted)] font-mono-display truncate">{tr("page_id: {page_id}", { page_id: c.page_id })}
                </div>
                {c.has_ig ? (
                  <div className="mt-1 inline-flex items-center gap-1.5 text-xs px-1.5 py-0.5 border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800">
                    <Instagram className="w-3 h-3" />@{c.ig_username || c.ig_user_id}
                    <CheckCircle2 className="w-3 h-3" />
                  </div>
                ) : (
                  <div className="mt-1 inline-flex items-center gap-1.5 text-xs px-1.5 py-0.5 border border-amber-200 bg-amber-50 text-amber-800">
                    <AlertTriangle className="w-3 h-3" />{tr("No Instagram Business account linked to this Page")}</div>
                )}
              </div>
              <div className="shrink-0 flex flex-col items-stretch gap-1">
                <button
                  data-testid={`social-default-${c.page_id}`}
                  onClick={() => setDefault.mutate(c.page_id)}
                  disabled={
                    setDefault.isPending || c.is_default || c.hidden
                  }
                  title={
                    c.hidden
                      ? tr("Hidden Pages can't be the default — click 'Show here' first")
                      : c.is_default
                      ? tr("Already the default Page for this property")
                      : tr("Pre-select this Page in every share panel on this property")
                  }
                  className={
                    "inline-flex items-center gap-1 text-xs px-2 py-1 border disabled:cursor-not-allowed " +
                    (c.is_default
                      ? "border-amber-300 bg-amber-50 text-amber-800"
                      : "border-transparent hover:border-amber-300 hover:bg-amber-50 text-[var(--ink-muted)] hover:text-amber-800 disabled:opacity-40")
                  }
                >
                  <Star
                    className={
                      "w-3 h-3 " + (c.is_default ? "fill-current" : "")
                    }
                  />
                  {c.is_default ? tr("Default") : tr("Make default")}
                </button>
                <button
                  data-testid={`social-visibility-${c.page_id}`}
                  onClick={() =>
                    setVisibility.mutate({
                      pageId: c.page_id,
                      hidden: !c.hidden,
                    })
                  }
                  disabled={setVisibility.isPending}
                  title={
                    c.hidden
                      ? tr("Show on this property's share menus")
                      : tr("Hide from this property's share menus (safe — doesn't disconnect)")
                  }
                  className={
                    "inline-flex items-center gap-1 text-xs px-2 py-1 border " +
                    (c.hidden
                      ? "border-neutral-300 hover:border-emerald-300 hover:bg-emerald-50 text-neutral-600 hover:text-emerald-700"
                      : "border-transparent hover:border-neutral-300 hover:bg-neutral-100 text-[var(--ink-muted)]")
                  }
                >
                  {c.hidden ? (
                    <>
                      <Eye className="w-3 h-3" />{tr("Show here")}</>
                  ) : (
                    <>
                      <EyeOff className="w-3 h-3" />{tr("Hide here")}</>
                  )}
                </button>
                <button
                  data-testid={`social-disconnect-${c.page_id}`}
                  onClick={() => {
                    if (
                      // eslint-disable-next-line no-alert
                      window.confirm(
                        tr("Disconnect \"{page_name}\" for real? This removes the access token — you'll need to reconnect via OAuth to publish again.", { page_name: c.page_name }),
                      )
                    ) {
                      disconnect.mutate(c.page_id);
                    }
                  }}
                  disabled={disconnect.isPending}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-transparent hover:border-red-200 hover:bg-red-50 text-[var(--ink-muted)] hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />{tr("Disconnect")}</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Diagnostic block — shows exactly what Meta returned on the last
          OAuth attempt. Useful when the flow "succeeded" on Facebook's
          side but no pages appeared here (usually a permission toggle
          the user unchecked on the grant screen). */}
      {diag && (
        <div className="border border-[var(--border)] bg-neutral-50 text-xs">
          <button
            data-testid="meta-diag-toggle"
            onClick={() => setDiagOpen((v) => !v)}
            className="w-full flex items-center gap-1.5 px-3 py-2 hover:bg-neutral-100"
          >
            {diagOpen ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            {trRich("<0>Last Facebook connection attempt · {pages_saved}/{pages_returned} pages saved</0>", { pages_saved: diag.pages_saved, pages_returned: diag.pages_returned }, [<span className="overline" />])}
            {diag.pages_skipped_no_token > 0 && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase px-1.5 py-0.5 border border-red-200 bg-red-50 text-red-700">
                <AlertTriangle className="w-3 h-3" />
                {tr("{pages_skipped_no_token} skipped", { pages_skipped_no_token: diag.pages_skipped_no_token })}</span>
            )}
          </button>
          {diagOpen && (
            <div className="px-3 pb-3 space-y-2">
              <div className="text-[var(--ink-muted)]">{tr("At {value}", { value: new Date(diag.at).toLocaleString(uiLocale()) })}
              </div>
              <div className="text-[var(--ink-muted)] font-mono-display truncate">{tr("redirect_uri: {redirect_uri}", { redirect_uri: diag.redirect_uri })}
              </div>
              {diag.pages_returned === 0 ? (
                <p className="text-red-700">{tr("Meta returned 0 pages. Check that you’re an admin of at least one Facebook Page under this account.")}</p>
              ) : (
                <div className="mt-1">
                  <div className="font-medium text-[var(--ink)] mb-1">{tr("Pages Meta returned:")}</div>
                  <ul className="space-y-1">
                    {(diag.page_names || []).map((name, i) => {
                      const ok = diag.access_token_flags?.[i];
                      const tasks = diag.tasks_per_page?.[i] || [];
                      return (
                        <li
                          key={i}
                          className="flex items-start gap-2 border border-[var(--border)] bg-white px-2 py-1.5"
                        >
                          {ok ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
                          ) : (
                            <AlertTriangle className="w-3.5 h-3.5 text-red-700 mt-0.5 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="font-medium">
                              {name || tr("(unnamed page {i})", { i: diag.page_ids?.[i] })}
                            </div>
                            <div className="text-[var(--ink-muted)]">{tr("access_token: {value} · tasks: {value2}", { value: ok ? tr("✅ present") : tr("❌ missing"), value2: tasks.length ? tasks.join(", ") : tr("(none)") })}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {diag.pages_skipped_no_token > 0 && (
                <div className="mt-2 p-2 border border-amber-200 bg-amber-50 text-amber-900">
                  <div className="font-medium mb-1">{tr("Why some pages have no token:")}</div>{trRich("On Facebook’s grant screen, users can uncheck individual permissions. Click <0>Connect Facebook</0> again and make sure <1>Post to Pages</1>, <2>Manage Facebook Pages</2>, and <3>Post to Instagram</3> stay enabled, AND that the pages you want are ticked in the “which pages?” selector.", null, [<em />, <strong />, <strong />, <strong />])}</div>
              )}

              {/* Full per-endpoint Meta probe trace — invaluable for
                  diagnosing "0 pages returned" bugs since it shows the
                  raw output of every Graph API endpoint we hit. */}
              {diag.meta_probe && (
                <div className="mt-3 p-2 border border-[var(--border)] bg-white">
                  <div className="font-medium mb-1 text-[var(--ink)]">{tr("Meta endpoint probe:")}</div>
                  {diag.meta_probe.hint && (
                    <div className="mb-2 p-2 border border-amber-200 bg-amber-50 text-amber-900">
                      💡 {diag.meta_probe.hint}
                    </div>
                  )}
                  <pre className="text-[10px] whitespace-pre-wrap break-words font-mono-display text-[var(--ink-muted)] max-h-64 overflow-y-auto">
                    {JSON.stringify(diag.meta_probe, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
